// Typed API client for the Treasury Ledger Payout v1 CRM.
//
// Bound to the BFF surface shipped in gao-id-worker #192 (read), #194
// (approve), #195 (record-tx + verify):
//   GET  /v2/admin/bff/treasury/summary
//   GET  /v2/admin/bff/partner-payables
//   GET  /v2/admin/bff/partner-payables/:id
//   POST /v2/admin/bff/partner-payables/:id/approve     (owner-gated)
//   POST /v2/admin/bff/partner-payables/:id/record-tx   (owner-gated)
//
// HARD RULES:
//   * The only two mutations are `approve` and `record-tx`. Both are
//     owner-gated server-side (session + trusted device + audit). The
//     browser NEVER broadcasts/signs/sends a transaction: `approve` only
//     marks a liability approved; `record-tx` only POSTs a txHash of a
//     transfer the operator already performed off-band, which the BE then
//     verifies on-chain read-only. There is NO wallet / signer / private
//     key anywhere in this module.
//   * The browser carries only the bearer session token (attached by
//     `apiCall`). No HMAC, no service secret.
//   * No proposalId / invoiceId / escrow-bucket concept exists in any
//     shape below — the BE never sends them and the CRM never asks.
//
// Response shapes mirror the BE route source byte-for-byte
// (gao-id-worker src/routes/admin-bff-partner-payables.ts). The BE is
// the single source of truth for every money value and every
// status / nextAction / blockingReason string; the CRM only renders
// what the BE computed.

import { apiCall } from "./client";

// ─── Money shapes (BE formatters fmtMinor / fmtBaseUnits) ───────────

/** A USD-minor money value. `displayAmount` ("59.70 USD") is the
 *  primary, must-show rendering; `rawAmount` ("5,970 minor") is the
 *  exact integer with separators, shown secondary. */
export interface MoneyMinor {
  minor: number;
  human: string;
  token: string;
  decimals: number;
  displayAmount: string;
  rawAmount: string;
}

/** A token-base-unit money value (e.g. on-chain USDC, 6 decimals).
 *  Same display contract as `MoneyMinor` but with no `minor` field —
 *  the BE `fmtBaseUnits` omits it. */
export interface MoneyBase {
  human: string;
  token: string;
  decimals: number;
  displayAmount: string;
  rawAmount: string;
}

// ─── Status / action enums (BE-owned) ───────────────────────────────

export type PayableStatus = "ready" | "pending" | "paid" | "blocked" | "none";

/** The four operator-facing tabs. `none` is never a tab — it only
 *  appears as a row status for partners with nothing to pay. */
export type PayableTab = "ready" | "pending" | "paid" | "blocked";

// ─── List rows: GET /partner-payables → { items } ───────────────────

export interface PartnerPayableRow {
  /** Latest payout_request id when one exists, else the identityId. */
  id: string;
  identityId: string;
  partner: { refCode: string | null; identityId: string };
  destinationShort: string | null;
  /** Available-to-pay (max(0, earned − paid − open − legacy)). */
  amount: MoneyMinor;
  earned: MoneyMinor;
  paid: MoneyMinor;
  openRequest: MoneyMinor;
  status: PayableStatus;
  requestStatus: string | null;
  nextAction: string;
  blockingReason: string | null;
}

export interface PartnerPayablesListResponse {
  items: PartnerPayableRow[];
}

// ─── Detail: GET /partner-payables/:id ──────────────────────────────

export interface PartnerPayableRequestView {
  payoutId: string;
  status: string;
  amount: MoneyMinor;
  destination?: string | null;
  destinationShort: string | null;
  requestedAt: string;
  rejectionReason: string | null;
}

export interface PartnerPayableRiskCheck {
  check: string;
  ok: boolean;
}

/** Latest payout_execution surfaced on the detail (BE #195). null when
 *  no txHash has been recorded yet. */
export interface PayoutExecutionView {
  status: string; // submitted | verified | failed
  txHash: string | null;
  txHashShort: string | null;
  blockNumber: number | null;
  verifiedAt: string | null;
}

export interface PartnerPayableDetailResponse {
  identityId: string;
  partner: { refCode: string | null; identityId: string; partnerApproved: boolean };
  /** Active payout request's own destination (source of truth), else the
   *  on-file payout address when no request exists. */
  destination: string | null;
  destinationShort: string | null;
  /** Partner's saved/on-file payout wallet — secondary info only. */
  onFilePayoutAddress?: string | null;
  onFilePayoutAddressShort?: string | null;
  payableSummary: {
    earned: MoneyMinor;
    paid: MoneyMinor;
    openRequest: MoneyMinor;
    available: MoneyMinor;
  };
  request: PartnerPayableRequestView | null;
  execution: PayoutExecutionView | null;
  risk: PartnerPayableRiskCheck[];
  status: PayableStatus;
  nextAction: string;
  blockingReason: string | null;
}

// ─── Treasury summary: GET /treasury/summary ────────────────────────

export type TreasuryBalance =
  | { available: false; reason: string }
  | ({ available: true } & MoneyBase);

/** A composition/balance bucket — same discriminated shape as the treasury
 *  balance: either an available money value (base units) or an
 *  unavailable marker with a reason. */
export type CompositionBucket =
  | { available: false; reason: string }
  | ({ available: true } & MoneyBase);

/** Treasury Accounting v2 buckets (BE #198). All optional/defensive so the
 *  page never crashes on a partially-missing shape. */
export interface TreasuryComposition {
  treasuryWalletBalance?: CompositionBucket;
  escrowTotalBalance?: CompositionBucket;
  v2EscrowTreasuryWithdrawable?: CompositionBucket;
  v3LockedDeposits?: CompositionBucket;
}

export interface PartnerAccountingCounts {
  partnersTotal?: number;
  partnersWithAvailable?: number;
  openValidRequests?: number;
  openBlockedRequests?: number;
  paidRequests?: number;
  notMaturedRewards?: number;
}

export interface PartnerAccounting {
  totalEarnedMinor?: number;
  notMaturedMinor?: number;
  maturedEligibleMinor?: number;
  partnerMaturedLiabilityMinor?: number;
  availableToRequestMinor?: number;
  openValidRequestsMinor?: number;
  openBlockedRequestsMinor?: number;
  paidVerifiedMinor?: number;
  creditLiabilityMinor?: number;
  estimatedOperatingTreasuryMinor?: number | null;
  /** Pre-formatted human + raw mirrors (USD minor). */
  display?: {
    totalEarned?: MoneyMinor;
    notMatured?: MoneyMinor;
    maturedEligible?: MoneyMinor;
    partnerMaturedLiability?: MoneyMinor;
    availableToRequest?: MoneyMinor;
    openValidRequests?: MoneyMinor;
    openBlockedRequests?: MoneyMinor;
    paidVerified?: MoneyMinor;
    creditLiability?: MoneyMinor;
    estimatedOperatingTreasury?: MoneyMinor | null;
  };
  counts?: PartnerAccountingCounts;
}

export interface TreasurySummaryResponse {
  treasuryAddress: string | null;
  treasuryAddressShort: string | null;
  chainId: number;
  token: string;
  treasuryBalance: TreasuryBalance;
  /** Corrected: now = partner matured liability (BE #198), no open-request
   *  overcount. Kept for backward compatibility. */
  partnerPayableReserve: MoneyMinor;
  // ── Treasury Accounting v2 (BE #198) — optional/defensive ──
  treasuryComposition?: TreasuryComposition;
  partnerAccounting?: PartnerAccounting;
  warnings?: string[];
}

// ─── Read-only calls (GET only; no mutation exists in this module) ──

export async function getTreasurySummary(): Promise<TreasurySummaryResponse> {
  return apiCall<TreasurySummaryResponse>("/v2/admin/bff/treasury/summary");
}

export interface ListPartnerPayablesInput {
  tab?: PayableTab;
}

export async function listPartnerPayables(
  input: ListPartnerPayablesInput = {},
): Promise<PartnerPayablesListResponse> {
  const query: Record<string, string | undefined> = {};
  if (input.tab) query["tab"] = input.tab;
  return apiCall<PartnerPayablesListResponse>(
    "/v2/admin/bff/partner-payables",
    { query },
  );
}

export async function getPartnerPayable(
  id: string,
): Promise<PartnerPayableDetailResponse> {
  return apiCall<PartnerPayableDetailResponse>(
    `/v2/admin/bff/partner-payables/${encodeURIComponent(id)}`,
  );
}

// ─── Owner-gated mutations (no wallet tx; BE re-checks + audits) ─────

/** Response of POST /partner-payables/:id/approve (BE #194). Approves a
 *  liability (requested → awaiting_tx). No txHash, no broadcast. */
export interface ApprovePayoutResponse {
  ok: true;
  id: string;
  identityId: string;
  status: "awaiting_tx";
  amountMinor: number;
  currency: string;
  displayAmount: string;
  destination: string;
  approvalId: string;
  nextAction: "record_tx";
  approvedAt: string;
}

/** Response of POST /partner-payables/:id/record-tx (BE #195). Records the
 *  txHash of an operator-sent transfer + the read-only verify outcome. */
export interface RecordTxResponse {
  ok: true;
  id: string;
  identityId: string;
  requestStatus: "submitted" | "paid" | "awaiting_tx";
  execution: {
    id: string;
    status: "submitted" | "verified" | "failed";
    txHash: string;
    blockNumber: number | null;
    amountBaseUnits: string;
  };
  verification: { outcome: "verified" | "pending" | "failed"; reason: string | null };
  nextAction: "verify" | "none" | "record_tx";
}

/** Approve a payout request. The BE marks the liability approved and gates
 *  on owner session + trusted device; the browser sends NO transaction. */
export async function approvePartnerPayable(id: string): Promise<ApprovePayoutResponse> {
  return apiCall<ApprovePayoutResponse>(
    `/v2/admin/bff/partner-payables/${encodeURIComponent(id)}/approve`,
    { method: "POST", body: {} },
  );
}

/** Record the txHash of a treasury transfer the operator ALREADY sent
 *  off-band. The BE verifies it on-chain read-only — the browser broadcasts
 *  / signs NOTHING. */
export async function recordPartnerPayableTx(
  id: string,
  txHash: string,
): Promise<RecordTxResponse> {
  return apiCall<RecordTxResponse>(
    `/v2/admin/bff/partner-payables/${encodeURIComponent(id)}/record-tx`,
    { method: "POST", body: { txHash } },
  );
}

/** Response of POST /partner-payables/:id/cancel (BE #197). Flips an OPEN
 *  request to cancelled. No funds move, no transaction. */
export interface CancelPayoutResponse {
  ok: true;
  id: string;
  identityId: string;
  status: "cancelled";
}

/** Cancel an OPEN payout request (requested/approved/awaiting_tx/submitted).
 *  Owner-gated server-side; reason required. Sends NO transaction. */
export async function cancelPartnerPayable(
  id: string,
  reason: string,
): Promise<CancelPayoutResponse> {
  return apiCall<CancelPayoutResponse>(
    `/v2/admin/bff/partner-payables/${encodeURIComponent(id)}/cancel`,
    { method: "POST", body: { reason } },
  );
}
